/**
 * QuestionCard — the one-question-at-a-time container for the questionnaire.
 *
 * Principle: one question on the screen at a time, and the answer is the
 * interesting part — not the form. The question is large at the top; the
 * input lives in the middle; nothing competes. Forward on tap (when the
 * component calls onDone); back is always available via the shell.
 *
 * One question, big type, no competing chrome.
 */
import type { ReactNode } from "react";
import { useT } from "../i18n";

interface QuestionCardProps {
  /** The question text — displayed large at the top. */
  question: string;
  /** Optional short subtext under the question. */
  subtext?: string;
  /** "n of m" shown quietly above the question. */
  position?: string;
  /** Whether this question may be skipped (non-gate fields). */
  skippable?: boolean;
  /** Called when the user taps "Skip for now". */
  onSkip?: () => void;
  /** The input/choice area. */
  children: ReactNode;
}

export function QuestionCard({
  question,
  subtext,
  position,
  skippable,
  onSkip,
  children,
}: QuestionCardProps) {
  const t = useT();
  return (
    <div className="q-card">
      <div className="q-card__head">
        {position ? (
          <span className="q-card__pos">{position}</span>
        ) : null}
        <h2 className="q-card__question">{question}</h2>
        {subtext ? (
          <p className="q-card__sub">{subtext}</p>
        ) : null}
      </div>
      <div className="q-card__body">{children}</div>
      {skippable && onSkip ? (
        <div className="q-card__skip">
          <button type="button" className="q-card__skip-btn" onClick={onSkip}>
            {t("questionnaire.skipForNow")}
          </button>
          <p className="q-card__skip-note">{t("questionnaire.skipConsequence")}</p>
        </div>
      ) : null}
    </div>
  );
}
